import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/AuthContext";
import { Bell, Warning, Clock, ShieldCheck, ListChecks, ArrowRight } from "@phosphor-icons/react";

const ICONS = { warning: Warning, clock: Clock, shield: ShieldCheck, checklist: ListChecks };
const SEV_STYLE = {
  critical: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
  warning:  { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  info:     { bg: "bg-blue-50",  text: "text-blue-700",  border: "border-blue-200",  dot: "bg-blue-500" },
};

export default function NotificationsBell() {
  const [data, setData] = useState({ items: [], total: 0, critical: 0, warning: 0, info: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const reload = () => api.get("/notifications").then(r => setData(r.data)).catch(()=>{});

  useEffect(() => {
    reload();
    const t = setInterval(reload, 60000); // poll every 60s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onClickItem = (item) => {
    setOpen(false);
    if (item.link) nav(item.link);
  };

  const badge = data.total;
  const hasCritical = data.critical > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(o => !o); if (!open) reload(); }}
        className="relative w-10 h-10 rounded-md hover:bg-neutral-100 transition-colors flex items-center justify-center text-neutral-600 hover:text-neutral-900"
        data-testid="notifications-bell-btn"
        aria-label="Notificaciones"
      >
        <Bell size={20} weight={hasCritical ? "fill" : "duotone"} className={hasCritical ? "text-rose-500" : ""}/>
        {badge > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-white ${hasCritical ? 'bg-rose-500' : 'bg-amber-500'}`} data-testid="notifications-badge">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[420px] max-w-[calc(100vw-2rem)] bg-white border border-neutral-200 rounded-md shadow-xl z-50 anim-fade-up" data-testid="notifications-dropdown">
          <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Centro de alertas</div>
              <h3 className="font-display text-lg font-bold tracking-tight">Notificaciones</h3>
            </div>
            <div className="flex gap-1.5">
              {data.critical > 0 && <span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded text-xs font-semibold border border-rose-100">{data.critical} crítico{data.critical===1?"":"s"}</span>}
              {data.warning > 0 && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-semibold border border-amber-100">{data.warning} preventivo{data.warning===1?"":"s"}</span>}
              {data.info > 0 && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-semibold border border-blue-100">{data.info} info</span>}
            </div>
          </div>

          <div className="max-h-[480px] overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="py-12 text-center text-neutral-400">
                <Bell size={42} className="mx-auto mb-2 text-neutral-300" weight="duotone"/>
                <p className="text-sm">Sin alertas pendientes</p>
                <p className="text-xs mt-1 text-neutral-400">Todo está al día.</p>
              </div>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {data.items.map(item => {
                  const Icon = ICONS[item.icon] || Bell;
                  const style = SEV_STYLE[item.severity] || SEV_STYLE.info;
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => onClickItem(item)}
                        className="w-full text-left p-3.5 hover:bg-neutral-50 transition-colors flex items-start gap-3 group"
                        data-testid={`notification-item-${item.id}`}
                      >
                        <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${style.bg} ${style.text} border ${style.border}`}>
                          <Icon size={16} weight="duotone"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}></span>
                            <div className="font-semibold text-sm text-neutral-900 truncate">{item.title}</div>
                          </div>
                          <div className="text-xs text-neutral-500 mt-0.5 truncate">{item.subtitle}</div>
                        </div>
                        <ArrowRight size={14} className="text-neutral-300 group-hover:text-neutral-600 transition-colors flex-shrink-0 mt-2"/>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {data.items.length > 0 && (
            <div className="p-3 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between text-xs text-neutral-500">
              <span>Mostrando {data.items.length} de {data.total}</span>
              <span>Actualización cada 60s</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
